import Swiper from "./swiper-bundle.esm.browser.min.js";

const MOBILE_BREAKPOINT = 769;
const WIDE_BREAKPOINT = 1200;

class ClinicalEvidence extends HTMLElement {
  constructor() {
    super();
    this._swiper = null;
    this._activeContext = null;
    this._resizeTimer = null;
    this._initialized = false;
    this._accordionObserver = null;
    this._handleResize = this._handleResize.bind(this);
    this._handleSectionLoad = this._handleSectionLoad.bind(this);
    this._handleSectionUnload = this._handleSectionUnload.bind(this);
  }

  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;
    document.addEventListener("shopify:section:load", this._handleSectionLoad);
    document.addEventListener("shopify:section:unload", this._handleSectionUnload);
    window.addEventListener("resize", this._handleResize, { passive: true });
    this._setupAccordionObserver();
    this._updateLayout();
  }

  disconnectedCallback() {
    this._initialized = false;
    this._destroySwiper();
    this._teardownAccordionObserver();
    window.removeEventListener("resize", this._handleResize);
    document.removeEventListener("shopify:section:load", this._handleSectionLoad);
    document.removeEventListener("shopify:section:unload", this._handleSectionUnload);
    if (this._resizeTimer) {
      clearTimeout(this._resizeTimer);
      this._resizeTimer = null;
    }
  }

  _isOurSection(event) {
    const section = this.closest(".shopify-section");
    return section && (event.target === section || section.contains(event.target));
  }

  _handleSectionLoad(event) {
    if (!this._isOurSection(event)) return;
    this._destroySwiper();
    this._setupAccordionObserver();
    this._updateLayout();
  }

  _handleSectionUnload(event) {
    if (!this._isOurSection(event)) return;
    this._destroySwiper();
    this._teardownAccordionObserver();
  }

  _readConfig() {
    const slideCount = parseInt(this.dataset.slideCount || "0", 10);
    return {
      slideCount,
      sectionId: this.dataset.sectionId || "",
      spaceBetween: parseInt(this.dataset.spaceBetween || "20", 10),
      mobileSlidesPerView: parseFloat(this.dataset.slidesPerViewMobile || "1.2"),
      slidesPerViewDesktop: parseFloat(this.dataset.slidesPerViewDesktop || "3.8"),
      slidesPerViewWide: parseFloat(this.dataset.slidesPerViewWide || "3.8"),
      showArrows: this.dataset.showArrows === "true",
      showPagination: this.dataset.showPagination === "true",
      enableLoop: this.dataset.enableLoop === "true",
      enableAutoplay: this.dataset.enableAutoplay === "true",
      autoplaySpeed: parseInt(this.dataset.autoplaySpeed || "5000", 10),
      pauseOnHover: this.dataset.pauseOnHover !== "false",
      pauseOnInteraction: this.dataset.pauseOnInteraction === "true",
    };
  }

  _isMobile() {
    return window.innerWidth < MOBILE_BREAKPOINT;
  }

  _getActiveContext() {
    return this._isMobile() ? "mobile" : "desktop";
  }

  _getContextRoot(context = this._getActiveContext()) {
    return context === "mobile"
      ? this.querySelector(".ce__mobile")
      : this.querySelector(".ce__desktop");
  }

  _shouldUseSwiper(config) {
    return config.slideCount > 1;
  }

  _isAccordionOpen() {
    const accordion = this.querySelector(".ce__accordion--main");
    return accordion?.dataset.open === "true";
  }

  _setupAccordionObserver() {
    this._teardownAccordionObserver();
    const accordion = this.querySelector(".ce__accordion--main");
    if (!accordion) return;

    this._accordionObserver = new MutationObserver(() => {
      if (!this._isMobile() || !this._swiper || this._swiper.destroyed) return;
      if (this._isAccordionOpen()) {
        requestAnimationFrame(() => this._swiper?.update());
      }
    });

    this._accordionObserver.observe(accordion, {
      attributes: true,
      attributeFilter: ["data-open"],
    });
  }

  _teardownAccordionObserver() {
    if (this._accordionObserver) {
      this._accordionObserver.disconnect();
      this._accordionObserver = null;
    }
  }

  _cleanupSwiperClasses(context) {
    const root = this._getContextRoot(context);
    if (!root) return;

    const swiperEl = root.querySelector("[data-swiper]");
    const container = root.querySelector("[data-swiper-container]");
    const slides = root.querySelectorAll("[data-swiper-slide]");

    if (swiperEl) {
      swiperEl.classList.remove("swiper", "ce__swiper", "swiper-initialized");
    }
    if (container) {
      container.classList.remove("swiper-wrapper");
    }
    slides.forEach((slide) => slide.classList.remove("swiper-slide"));
  }

  _applyStaticMode(context) {
    const root = this._getContextRoot(context);
    if (!root) return;

    root.classList.add("ce--static");
    root.classList.remove("ce--swiper-active");
    this._cleanupSwiperClasses(context);
    this._syncSlideAccessibility(0, context);
  }

  _prepareSwiperStructure(context) {
    const root = this._getContextRoot(context);
    if (!root) return null;

    const swiperEl = root.querySelector("[data-swiper]");
    const container = root.querySelector("[data-swiper-container]");
    const slideEls = root.querySelectorAll("[data-swiper-slide]");

    if (!swiperEl || !container || slideEls.length === 0) return null;

    swiperEl.classList.add("swiper", "ce__swiper");
    container.classList.add("swiper-wrapper");
    slideEls.forEach((slide) => slide.classList.add("swiper-slide"));

    return { root, slideEls };
  }

  _syncSlideAccessibility(activeIndex, context = this._activeContext) {
    const root = this._getContextRoot(context);
    if (!root) return;

    const slides = root.querySelectorAll("[data-swiper-slide]");
    slides.forEach((slide, index) => {
      slide.setAttribute("aria-hidden", index === activeIndex ? "false" : "true");
    });
  }

  _initSwiper(config, context) {
    const structure = this._prepareSwiperStructure(context);
    if (!structure) return;

    const { root, slideEls } = structure;
    const canNavigate = slideEls.length > 1;
    const desktopSpv = Math.min(config.slidesPerViewDesktop, config.slideCount);
    const wideSpv = Math.min(config.slidesPerViewWide, config.slideCount);
    const mobileSpv = Math.min(config.mobileSlidesPerView, config.slideCount);
    const isMobile = context === "mobile";
    const canLoop =
      config.enableLoop &&
      canNavigate &&
      slideEls.length > (isMobile ? mobileSpv : desktopSpv);

    root.classList.add("ce--swiper-active");
    root.classList.remove("ce--static");
    this.classList.add("ce--swiper-active");
    this._activeContext = context;

    const swiperConfig = {
      slidesPerView: isMobile ? mobileSpv : desktopSpv,
      spaceBetween: config.spaceBetween,
      watchOverflow: true,
      grabCursor: canNavigate,
      speed: 350,
      keyboard: {
        enabled: true,
        onlyInViewport: true,
      },
      a11y: {
        enabled: true,
        prevSlideMessage: "Previous evidence card",
        nextSlideMessage: "Next evidence card",
      },
    };

    if (!isMobile) {
      swiperConfig.breakpoints = {
        [WIDE_BREAKPOINT]: {
          slidesPerView: wideSpv,
          spaceBetween: config.spaceBetween,
        },
      };
    }

    if (canLoop) {
      swiperConfig.loop = true;
    }

    if (config.showArrows && canNavigate) {
      const navScope = isMobile
        ? `--${config.sectionId}-mobile`
        : `--${config.sectionId}`;
      const nextEl = root.querySelector(`.ce__nav-next${navScope}`);
      const prevEl = root.querySelector(`.ce__nav-prev${navScope}`);
      if (nextEl && prevEl) {
        swiperConfig.navigation = { nextEl, prevEl };
      }
    }

    if (config.showPagination && canNavigate) {
      const suffix = isMobile
        ? `.ce__pagination--${config.sectionId}-mobile`
        : `.ce__pagination--${config.sectionId}`;
      const paginationEl = root.querySelector(suffix);
      if (paginationEl) {
        swiperConfig.pagination = {
          el: paginationEl,
          clickable: true,
          renderBullet: (index, className) => {
            const num = index + 1;
            const total = config.slideCount;
            return `<button type="button" class="${className}" aria-label="Go to card ${num} of ${total}"></button>`;
          },
        };
      }
    }

    if (config.enableAutoplay && canNavigate) {
      swiperConfig.autoplay = {
        delay: config.autoplaySpeed,
        disableOnInteraction: config.pauseOnInteraction,
        pauseOnMouseEnter: config.pauseOnHover,
      };
    }

    const existingOn = swiperConfig.on || {};
    swiperConfig.on = {
      ...existingOn,
      afterInit: (swiper) => {
        existingOn.afterInit?.(swiper);
        this._syncSlideAccessibility(swiper.realIndex ?? swiper.activeIndex, context);
      },
      slideChangeTransitionEnd: (swiper) => {
        existingOn.slideChangeTransitionEnd?.(swiper);
        this._syncSlideAccessibility(swiper.realIndex ?? swiper.activeIndex, context);
      },
      resize: (swiper) => {
        existingOn.resize?.(swiper);
        swiper.update();
      },
    };

    const swiperEl = root.querySelector("[data-swiper]");
    this._swiper = new Swiper(swiperEl, swiperConfig);
    requestAnimationFrame(() => this._swiper?.update());
  }

  _destroySwiper() {
    if (this._swiper && !this._swiper.destroyed) {
      this._swiper.destroy(true, true);
    }
    this._swiper = null;

    if (this._activeContext) {
      this._cleanupSwiperClasses(this._activeContext);
    } else {
      this._cleanupSwiperClasses("desktop");
      this._cleanupSwiperClasses("mobile");
    }

    this._activeContext = null;
    this.classList.remove("ce--swiper-active");
    this.querySelector(".ce__desktop")?.classList.remove("ce--swiper-active", "ce--static");
    this.querySelector(".ce__mobile")?.classList.remove("ce--swiper-active", "ce--static");
  }

  _updateLayout() {
    const config = this._readConfig();
    const context = this._getActiveContext();
    const shouldUseSwiper = this._shouldUseSwiper(config);
    const isSwiperActive = Boolean(this._swiper);

    if (!shouldUseSwiper) {
      this._destroySwiper();
      this._applyStaticMode("desktop");
      this._applyStaticMode("mobile");
      return;
    }

    if (isSwiperActive) {
      this._destroySwiper();
    }

    this._initSwiper(config, context);
  }

  _handleResize() {
    if (this._resizeTimer) clearTimeout(this._resizeTimer);
    this._resizeTimer = setTimeout(() => {
      const config = this._readConfig();
      const context = this._getActiveContext();
      const shouldUseSwiper = this._shouldUseSwiper(config);
      const isSwiperActive = Boolean(this._swiper);

      if (shouldUseSwiper !== isSwiperActive || this._activeContext !== context) {
        this._updateLayout();
        return;
      }

      if (this._swiper && !this._swiper.destroyed) {
        this._swiper.update();
      }
    }, 150);
  }
}

if (!customElements.get("clinical-evidence")) {
  customElements.define("clinical-evidence", ClinicalEvidence);
}
